<script setup lang="ts">
import { computed, isVNode, ref, watch } from 'vue'
import { AlertTriangle } from '@lucide/vue'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { shadDialogRequest } from './dialogService'

const inputValue = ref('')
const inputError = ref('')

const request = computed(() => shadDialogRequest.value)
const isOpen = computed({
  get: () => Boolean(request.value?.open),
  set: (open) => {
    if (!open) {
      handleClose()
    }
  },
})
const messageVNode = computed(() =>
  request.value?.message && isVNode(request.value.message) ? request.value.message : null,
)
const messageText = computed(() =>
  typeof request.value?.message === 'string' ? request.value.message : '',
)
const confirmText = computed(() => request.value?.options.confirmButtonText || 'Confirm')
const cancelText = computed(() => request.value?.options.cancelButtonText || 'Cancel')
const showCancel = computed(() => request.value?.kind !== 'alert')

watch(
  () => request.value?.id,
  () => {
    inputValue.value = ''
    inputError.value = ''
  },
)

const settle = (action: 'confirm' | 'cancel' | 'close') => {
  const current = request.value
  if (!current) return

  if (action === 'confirm') {
    if (current.kind === 'prompt') {
      const validator = current.options.inputValidator
      const validation = validator?.(inputValue.value)
      if (typeof validation === 'string') {
        inputError.value = validation
        return
      }
      if (validation === false) {
        inputError.value = 'Invalid input'
        return
      }
      current.resolve({ value: inputValue.value })
    } else {
      current.resolve()
    }
  } else {
    current.reject({ action })
  }

  shadDialogRequest.value = null
}

const handleClose = () => {
  settle('close')
}
</script>

<template>
  <Dialog v-model:open="isOpen">
    <DialogContent
      v-if="request"
      class="sm:max-w-130"
      :show-close-button="!request.options.mandatory"
      @escape-key-down="(event) => request?.options.mandatory && event.preventDefault()"
      @pointer-down-outside="
        (event) => request?.options.closeOnClickModal === false && event.preventDefault()
      "
    >
      <DialogHeader>
        <DialogTitle class="flex items-center gap-2">
          <AlertTriangle
            v-if="request.options.variant === 'destructive'"
            class="size-4 text-red-500"
          />
          {{ request.title }}
        </DialogTitle>
        <DialogDescription v-if="messageText">{{ messageText }}</DialogDescription>
      </DialogHeader>

      <component :is="messageVNode" v-if="messageVNode" />

      <form v-if="request.kind === 'prompt'" class="space-y-2" @submit.prevent="settle('confirm')">
        <Input
          v-model="inputValue"
          :type="request.options.inputType || 'text'"
          :placeholder="request.options.inputPlaceholder"
          autofocus
        />
        <p v-if="inputError" class="text-xs text-red-600">{{ inputError }}</p>
      </form>

      <DialogFooter>
        <Button v-if="showCancel" type="button" variant="outline" @click="settle('cancel')">
          {{ cancelText }}
        </Button>
        <Button
          type="button"
          :variant="request.options.variant === 'destructive' ? 'destructive' : 'default'"
          @click="settle('confirm')"
        >
          {{ confirmText }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
